import { useMemo } from "react";

import { formatDate, formatDayMonth } from "@/modules/carga-trabalho";
import type { CargaTrabalhoIntervalo } from "@/modules/carga-trabalho";
import { useFornecedoresDisponiveis } from "@/modules/recursos";
import type { FornecedorDisponivel } from "@/modules/recursos";

import { obraTitulo } from "../../../resolver-conflito/resolver-conflito.utils";
import {
  FORNECEDORES_BUSCA_VAZIA_MESSAGE,
  FORNECEDORES_INDISPONIVEIS_MESSAGE,
  SEM_ALOCACAO_LABEL,
} from "../../resolver-conflito-fornecedor.constants";
import { calcularGeraConflito, plural } from "../../resolver-conflito-fornecedor.utils";
import type { GeraConflito } from "../../resolver-conflito-fornecedor.utils";
import type {
  FornecedoresEstado,
  SubstitutoFornecedorStepProps,
  SubstitutoFornecedorStepViewModel,
} from "./types-substituto-fornecedor-step";

function intervaloCurtoAno(intervalo: CargaTrabalhoIntervalo): string {
  return `${formatDayMonth(intervalo.inicio)} a ${formatDate(intervalo.fim)}`;
}

/** Texto de apoio do candidato: sem alocação ou onde ele já está e quantos dias sobrepõem. */
function candidatoDetalhe(item: FornecedorDisponivel, gera: GeraConflito | null): string {
  if (item.disponibilidade === "DISPONIVEL") return SEM_ALOCACAO_LABEL;
  if (!gera) return "Com alocação no período";
  return `Na ${obraTitulo(gera.obra)} · ${plural(gera.dias, "dia sobreposto", "dias sobrepostos")}`;
}

/**
 * Etapa 2 (Fornecedor): busca o substituto com a situação no período da alocação. "Disponível" =
 * sem alocação no período; "Gera conflito" = já tem alocação em outra obra nesse período (calculado
 * com a carga carregada; a seleção é permitida e o conflito passa a aparecer no cronograma).
 */
export function useSubstitutoFornecedorStep({
  fornecedor,
  alocacao,
  escopo,
  carga,
  substituto,
  search,
  erroSubstituto,
  escolherSubstituto,
  buscar,
}: SubstitutoFornecedorStepProps): SubstitutoFornecedorStepViewModel {
  const result = useFornecedoresDisponiveis({
    busca: search,
    dataInicio: alocacao.inicio,
    dataFim: alocacao.fim,
    ignorarObraId: alocacao.obra.id,
  });

  // O próprio fornecedor em conflito não pode substituir a si mesmo.
  const candidatos = useMemo(
    () =>
      result.fornecedores
        .filter((item) => item.id !== fornecedor.id)
        .map((item) => ({
          item,
          gera:
            item.disponibilidade === "INDISPONIVEL"
              ? calcularGeraConflito(
                  carga?.profissionais.find((outro) => outro.id === item.id),
                  alocacao,
                )
              : null,
        })),
    [result.fornecedores, fornecedor.id, carga, alocacao],
  );
  const temBusca = search.trim() !== "";
  const geraSelecionado = candidatos.find(
    ({ item, gera }) =>
      item.id === substituto?.id && item.disponibilidade === "INDISPONIVEL" && gera,
  )?.gera;

  let fornecedores: FornecedoresEstado = { tipo: "carregando" };
  if (result.status === "error") {
    fornecedores = { tipo: "erro" };
  } else if (result.status === "success") {
    fornecedores =
      candidatos.length === 0
        ? {
            tipo: "vazio",
            mensagem: temBusca
              ? FORNECEDORES_BUSCA_VAZIA_MESSAGE
              : FORNECEDORES_INDISPONIVEIS_MESSAGE,
          }
        : {
            tipo: "lista",
            semDisponiveis:
              !temBusca && candidatos.every(({ item }) => item.disponibilidade === "INDISPONIVEL"),
            opcoes: candidatos.map(({ item, gera }) => ({
              id: item.id,
              nome: item.nome,
              detalhe: candidatoDetalhe(item, gera),
              geraConflito: item.disponibilidade === "INDISPONIVEL",
              marcado: substituto?.id === item.id,
            })),
          };
  }

  return {
    fornecedorNome: fornecedor.nome,
    periodoInfo: escopo
      ? `${escopo.escopo} · ${obraTitulo(alocacao.obra)} (${intervaloCurtoAno(escopo)})`
      : `${obraTitulo(alocacao.obra)} (${intervaloCurtoAno(alocacao)})`,
    search,
    erroSubstituto,
    fornecedores,
    avisoGeraConflito:
      substituto && geraSelecionado
        ? `A ${substituto.nome} passa a ter ${plural(geraSelecionado.dias, "dia", "dias")} de conflito com a ${obraTitulo(geraSelecionado.obra)}. O conflito aparece no cronograma.`
        : null,
    escolherSubstituto: (fornecedorId) => {
      const escolhido = candidatos.find(({ item }) => item.id === fornecedorId)?.item;
      if (escolhido) escolherSubstituto(escolhido);
    },
    buscar,
  };
}
